import { useMemo, useContext } from 'react';
import { head } from 'utility/snippets';
import { useTranslation } from 'react-i18next';
import { getStepConfig } from 'pages/AutomationEngine/steps';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import {
  getCurrentExecution,
  getArchivedContext,
  getBranches,
} from 'pages/AutomationEngine/store/selectors';
import { ExecutionHistoryContext } from 'ts-components/AutomationEngine/ExecutionHistory/context';
import DraggableStepCard from '../../DraggableStepCard';
import TriggerCard from '../../TriggerCard';
import { StepCard } from '../../StepCard';
import MovingPlaceholder from '../../moving-placeholder/MovingPlaceholder';
import MovingGotoPlaceholder from '../../moving-placeholder/MovingGotoPlaceholder';
import MovingBranchPlaceholder from '../../moving-placeholder/MovingBranchPlaceholder';
import MovingConditionPlaceholder from '../../moving-placeholder/MovingConditionPlaceholder';
import MovingLoopPlaceholder from '../../moving-placeholder/MovingLoopPlaceholder';
import MovingExitLoopPlaceholder from '../../moving-placeholder/MovingExitLoopPlaceholder';
import MovingGoalPlaceholder from '../../moving-placeholder/MovingGoalPlaceholder';

import {
  errorByStatus,
  getGrandChildrenCount,
} from 'pages/AutomationEngine/ts-helpers';
import * as cardTypes from '__pages/AutomationEngine/Engine/Steps/type';
import {
  ExecutionHistory,
  HistoryStatus,
  Step,
  KizenNodeType,
  KizenTriggerType,
} from 'pages/AutomationEngine/types';
import { getSteps } from 'pages/AutomationEngine/store/selectors';
import type {
  AutomationExecution,
  AutomationExecutionStatus,
} from '@kizen/api/automation';
import { getStepIdPreview } from 'pages/AutomationEngine/Engine/utilities';

import { isStepUnreachable } from './unreachable';
import { StepMovingBlockerTypes } from 'pages/AutomationEngine/store/types';

import { getSubtypeLabel } from 'pages/AutomationEngine/helpers';
import { type FlowNodeType } from '../../../Flow/types';
import { getHistoryStatusColor } from './helpers';
import type { AutomationStep } from 'ts-components/Plugins/types';
import { ExecutionHistoryStep } from './types';
import { getStepMenuOption } from './stepMenuOptions';

const stepsThatCanBeRestartedByExecutionStatus: Record<HistoryStatus, boolean> =
  {
    pending: false,
    paused_before_execution: false,
    completed: true,
    failed: true,
    paused_by_failure: true,
    pending_throttled: false,
    pending_retry: false,
    debug_waiting: false,
    skipped_execution: true,
    paused_after_execution: true,
    cancelled: false,
    waiting_for_branches: false,
    blocked: false,
  };

const getShowDebugRestart = (
  history?: ExecutionHistory,
  executionStatus?: AutomationExecutionStatus
) =>
  (history?.status &&
    (stepsThatCanBeRestartedByExecutionStatus[history.status] ||
      (executionStatus !== 'active' && history.status === 'debug_waiting'))) ||
  false;

type UseExecutionHistory = {
  step: ExecutionHistoryStep;
  type: FlowNodeType;
  showMovingPlaceholder: boolean;
  onStartProp?: any;
  onStopProp?: any;
  scaleProp?: any;
  showMovingBlocker: StepMovingBlockerTypes;
};

const isCondition = (step: ExecutionHistoryStep) => step.type === 'condition';
const isInitializeVariable = (step: ExecutionHistoryStep) =>
  step.type === 'initialize_variable';

const isAction = (step: ExecutionHistoryStep) => step.prefix === 'action';

const stepTypeAllowedToShowRestart = (step: ExecutionHistoryStep) =>
  isCondition(step) || isInitializeVariable(step) || isAction(step);

const shouldShowDebugRestart = (
  step: ExecutionHistoryStep,
  executionStatus?: AutomationExecutionStatus
) => {
  if (!step?.history) {
    return false;
  }

  const latestHistory = head(step.history);
  return getShowDebugRestart(latestHistory, executionStatus);
};

export const useExecutionHistory = ({
  step,
  type,
  showMovingPlaceholder,
  showMovingBlocker,
  onStartProp,
  onStopProp,
  scaleProp,
}: UseExecutionHistory) => {
  const { t } = useTranslation();
  const automationSteps: AutomationStep<{ name: string }>[] =
    useAutomationsSelector((state: any) => state.automationPlugins);

  const stepType =
    step.type === 'goal' ? (step?.details?.type as KizenNodeType) : step.type;

  const stepConfig = getStepConfig(stepType);

  const inLoop = !!step.loopDepth;

  const {
    historyId,
    executionMode,
    getSelectedHistoryStep,
    showHistorySideBar,
    isHistoryFetching,
    canEditAutomation,
    runningDebugAction,
  } = useContext(ExecutionHistoryContext);

  const selectedHistoryStep = getSelectedHistoryStep();

  const steps: Step[] = useAutomationsSelector(getSteps);

  const execution: AutomationExecution =
    useAutomationsSelector(getCurrentExecution);

  const executionRanAsDebug = execution?.debug_mode === 'active';

  const archivedContext = useAutomationsSelector(getArchivedContext);

  const unreachable = useMemo(
    () => isStepUnreachable(step, steps),
    [step, steps]
  );

  const grandChildrenCount = useMemo(
    () => getGrandChildrenCount(steps, step.id),
    [steps, step.id]
  );

  const stepShowRestart = useMemo(() => {
    return (
      canEditAutomation &&
      executionRanAsDebug &&
      !archivedContext &&
      stepTypeAllowedToShowRestart(step) &&
      shouldShowDebugRestart(step, execution?.status)
    );
  }, [
    canEditAutomation,
    executionRanAsDebug,
    archivedContext,
    step,
    execution?.status,
  ]);

  const branches = useAutomationsSelector(getBranches);

  const {
    borderColor,
    CardComponent,
    menuOptions,
    historyMode,
    addMenu,
    unusedHistoryStep,
    focusedHistoryStep,
    scale,
    onStart,
    onStop,
    showErrors,
    showRestart,
    subtypeLabel,
  } = useMemo(() => {
    const typeLabel =
      step.type === 'initialize_variable_add' || step.type === 'add_trigger'
        ? null
        : getSubtypeLabel(
            step.type === 'initialize_variable'
              ? {
                  type: step.type,
                  subtype: step.details?.variable?.dataType ?? '',
                }
              : step.type === 'goal'
                ? {
                    type: step.details?.type?.replace(
                      /_goal$/,
                      ''
                    ) as KizenTriggerType,
                  }
                : step.type === 'condition'
                  ? {
                      type: step.type,
                      subtype: step.details?.type as any,
                    }
                  : step.type === 'plugin_code_step'
                    ? {
                        type: step.type,
                        subtype:
                          automationSteps.find(
                            ({ plugin_api_name }) =>
                              plugin_api_name ===
                              step?.details?.pluginAppApiName
                          )?.actions?.[step?.details?.actionStepApiName]
                            ?.name ?? '',
                      }
                    : { type: step.type },
            t
          );

    // Item reference variables are only drawn in the execution and debug
    // views, where without a marker they read as a duplicate of the array
    // variable they draw from.
    const subtypeLabel =
      typeLabel && step.details?.variable?.isArrayItemVariable
        ? t('Item Reference: {{type}}', { type: typeLabel })
        : typeLabel;

    if (historyId) {
      const hasErrorByStatus =
        errorByStatus(step?.status!) ||
        (step?.history || []).some((record: ExecutionHistory) =>
          errorByStatus(record?.status)
        );

      const borderColor = `var(--color-${getHistoryStatusColor(
        runningDebugAction === step.history?.[0]?.id
          ? 'running'
          : step.history?.[0]?.status
      )})`;

      const isSelectedStep =
        showHistorySideBar &&
        selectedHistoryStep &&
        (step.id === selectedHistoryStep.stepId ||
          step.id === selectedHistoryStep.triggerId);

      const unusedHistoryStep = isHistoryFetching || !step.history;

      return {
        borderColor: unusedHistoryStep ? null : borderColor,
        CardComponent:
          (stepConfig.CardComponent && cardTypes[stepConfig.CardComponent]) ||
          (type === 'automationTrigger' && TriggerCard) ||
          StepCard,
        menuOptions: [],
        historyMode: true,
        addMenu: false,
        unusedHistoryStep,
        focusedHistoryStep: isSelectedStep && !isHistoryFetching,
        scale: null,
        onStart: null,
        onStop: null,
        showErrors: !!hasErrorByStatus,
        showRestart: stepShowRestart,
        subtypeLabel,
      };
    }

    const stepIdPreview = getStepIdPreview(step.id);
    const menuOptions = getStepMenuOption(
      step,
      stepConfig,
      grandChildrenCount,
      stepIdPreview,
      branches,
      inLoop,
      t
    );

    return {
      borderColor: step.hasError ? 'var(--color-button-warning-hover)' : null,
      CardComponent:
        (showMovingPlaceholder && MovingPlaceholder) ||
        (showMovingBlocker === 'goto' && MovingGotoPlaceholder) ||
        (showMovingBlocker === 'branch' && MovingBranchPlaceholder) ||
        (showMovingBlocker === 'goal' && MovingGoalPlaceholder) ||
        (showMovingBlocker === 'condition' && MovingConditionPlaceholder) ||
        (showMovingBlocker === 'loop' && MovingLoopPlaceholder) ||
        (showMovingBlocker === 'exit_loop' && MovingExitLoopPlaceholder) ||
        (stepConfig.CardComponent && cardTypes[stepConfig.CardComponent]) ||
        (type === 'automationTrigger' && TriggerCard) ||
        (stepConfig.movable && DraggableStepCard) ||
        StepCard,
      menuOptions,
      historyMode: false,
      // CardMenu renders its button regardless of what is in the list, so a
      // step with no actions must not be given a menu at all
      addMenu: !executionMode && menuOptions.length > 0,
      unusedHistoryStep: false,
      focusedHistoryStep: false,
      scale: scaleProp,
      onStart: onStartProp,
      onStop: onStopProp,
      showErrors: step.hasError,
      showRestart: false,
      subtypeLabel,
    };
  }, [
    historyId,
    step,
    stepConfig,
    showMovingPlaceholder,
    showMovingBlocker,
    type,
    grandChildrenCount,
    t,
    executionMode,
    scaleProp,
    onStartProp,
    onStopProp,
    showHistorySideBar,
    selectedHistoryStep,
    isHistoryFetching,
    stepShowRestart,
    branches,
    inLoop,
    automationSteps,
    runningDebugAction,
  ]);

  return {
    historyId,
    borderColor,
    CardComponent,
    menuOptions,
    historyMode,
    addMenu,
    unusedHistoryStep,
    focusedHistoryStep,
    scale,
    onStart,
    onStop,
    showErrors,
    unreachable,
    showRestart,
    executionMode,
    subtypeLabel,
  };
};
